// /register-sessions — Register session report (ops simulation T12). Lives
// beside Cash Closing, not in the plan-gated reports: like the shift session
// reports it is the drawer's own record, so it is never plan-gated.
//
// One card per register session: opening cash, sales, customers, discount,
// tax, returns / credit notes, cancelled bills, payment modes and the drawer
// closing — and under it the same figures per cashier. A session's totals are
// the sum of its cashier rows, so they always agree to the minor unit.
// Excel export is a CSV (opens in Excel); PDF is the browser's print dialog.

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { TimeRangeValue } from '@bbpos/ui';
import { Select } from '@bbpos/ui';
import { getSubunitMultiplier } from '@bbpos/utils';
import { formatPaise } from '@/utils/currency';
import {
  ReportPageShell,
  ReportFilterBar,
  ReportKpiRow,
  ReportExportMenu,
  ReportSkeleton,
  ReportError,
  useReportFilter,
  downloadCsv,
  DEFAULT_REPORT_PRESET,
  KpiIcons,
} from '@/features/reports';
import type { ReportExportFormat } from '@/features/reports';
import { useAuthStore } from '@/stores/use-auth-store';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';
import { registerService } from '@/services/register.service';
import type { RegisterSessionReportRow, SessionCashierRow } from '@/services/register.service';

const PAGE_SIZE = 50;

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
}

export function RegisterSessionsReportPage() {
  const [timeRange, setTimeRange] = useState<TimeRangeValue>(DEFAULT_REPORT_PRESET);
  const [locationId, setLocationId] = useState<string | undefined>(undefined);
  const [registerId, setRegisterId] = useState<string>('');
  const [status, setStatus] = useState<'' | 'OPEN' | 'CLOSED'>('');
  const [cashierId, setCashierId] = useState<string>('');
  const [page, setPage] = useState(1);
  const filter = useReportFilter(timeRange, locationId);
  const tenantId = useAuthStore((s) => s.profile?.tenant_id ?? 'anonymous');

  const currency = useTenantCurrency();
  const symbol = getCurrencySymbol(currency);
  const subunit = getSubunitMultiplier(currency);
  const decimals = Math.round(Math.log10(subunit));
  const money = (minor: number | null | undefined) => (minor == null ? '—' : formatPaise(minor, currency));

  const registersQuery = useQuery({
    queryKey: ['registers', tenantId, locationId],
    queryFn: () => registerService.list(locationId!),
    enabled: !!locationId,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['reports', 'register-sessions', tenantId, filter.range.from, filter.range.to, locationId ?? 'all', registerId, status, page],
    queryFn: () =>
      registerService.report({
        start_date: filter.range.from,
        end_date: filter.range.to,
        location_id: locationId,
        register_id: registerId || undefined,
        status: status || undefined,
        page,
        limit: PAGE_SIZE,
      }),
    staleTime: 30_000,
  });

  const allSessions = useMemo(() => data?.data ?? [], [data]);

  // Cashier filter: narrows each session to that cashier's rows (and drops
  // sessions they did not bill on), so the figures shown are theirs.
  const cashierOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const s of allSessions) {
      for (const c of s.cashiers) if (c.cashier_id) seen.set(c.cashier_id, c.cashier_name ?? 'Unknown');
    }
    return [...seen.entries()].map(([value, label]) => ({ value, label }));
  }, [allSessions]);

  const sessions = useMemo(() => {
    if (!cashierId) return allSessions;
    return allSessions
      .map((s) => ({ ...s, cashiers: s.cashiers.filter((c) => c.cashier_id === cashierId) }))
      .filter((s) => s.cashiers.length > 0);
  }, [allSessions, cashierId]);

  const kpis = useMemo(() => {
    const sumOf = (pick: (c: SessionCashierRow) => number) =>
      sessions.reduce((t, s) => t + s.cashiers.reduce((a, c) => a + pick(c), 0), 0);
    const diff = sessions.reduce((t, s) => t + (s.drawer.difference ?? 0), 0);
    return {
      net: sumOf((c) => c.net_total),
      bills: sumOf((c) => c.sales_count),
      returns: sumOf((c) => c.returns_total),
      diff,
    };
  }, [sessions]);

  const handleExport = (fmt: ReportExportFormat) => {
    if (fmt === 'pdf') {
      window.print();
      return;
    }
    if (fmt !== 'csv') return;
    type Line = { s: RegisterSessionReportRow; c: SessionCashierRow | null };
    const lines: Line[] = [];
    for (const s of sessions) {
      for (const c of s.cashiers) lines.push({ s, c });
      lines.push({ s, c: null });
    }
    const m = (v: number | null | undefined) => (v == null ? '' : (v / subunit).toFixed(decimals));
    const t = (l: Line) => l.c ?? (cashierId ? null : l.s.totals);
    downloadCsv(
      `register-sessions-${filter.range.from}-to-${filter.range.to}.csv`,
      [
        { header: 'Location', get: (l: Line) => l.s.location_name },
        { header: 'Register', get: (l: Line) => l.s.register_name },
        { header: 'Session', get: (l: Line) => l.s.session_number },
        { header: 'Status', get: (l: Line) => l.s.status },
        { header: 'Cashier', get: (l: Line) => (l.c ? l.c.cashier_name ?? 'Unknown' : 'SESSION TOTAL') },
        { header: 'Bills', get: (l: Line) => t(l)?.sales_count ?? '' },
        { header: `Sales (${symbol})`, get: (l: Line) => m(t(l)?.sales_total) },
        { header: `Discount (${symbol})`, get: (l: Line) => m(t(l)?.discount_total) },
        { header: `Tax (${symbol})`, get: (l: Line) => m(t(l)?.tax_total) },
        { header: 'Customers', get: (l: Line) => t(l)?.customers_count ?? '' },
        { header: 'Returns', get: (l: Line) => t(l)?.returns_count ?? '' },
        { header: `Returns (${symbol})`, get: (l: Line) => m(t(l)?.returns_total) },
        { header: 'Cancelled', get: (l: Line) => t(l)?.cancelled_count ?? '' },
        { header: `Net (${symbol})`, get: (l: Line) => m(t(l)?.net_total) },
        {
          header: 'Payment modes',
          get: (l: Line) =>
            (l.c ? l.c.payment_modes : l.s.payment_modes)
              .map((p) => `${p.payment_method} ${m(p.collected - p.refunded)}`)
              .join('; '),
        },
        { header: `Opening cash (${symbol})`, get: (l: Line) => (l.c ? '' : m(l.s.drawer.opening_cash)) },
        { header: `Expected cash (${symbol})`, get: (l: Line) => (l.c ? '' : m(l.s.drawer.expected_cash)) },
        { header: `Counted cash (${symbol})`, get: (l: Line) => (l.c ? '' : m(l.s.drawer.counted_cash)) },
        { header: `Difference (${symbol})`, get: (l: Line) => (l.c ? '' : m(l.s.drawer.difference)) },
        { header: 'Difference reason', get: (l: Line) => (l.c ? '' : l.s.drawer.difference_reason ?? '') },
      ],
      lines,
    );
  };

  const extraFilters = (
    <>
      {locationId && (
        <div className="w-full sm:w-44">
          <Select
            options={[
              { value: '', label: 'All registers' },
              ...(registersQuery.data ?? []).map((r) => ({ value: r.id, label: r.name })),
            ]}
            value={registerId}
            onChange={(v) => { setRegisterId(typeof v === 'string' ? v : ''); setPage(1); }}
            placeholder="All registers"
          />
        </div>
      )}
      <div className="w-full sm:w-36">
        <Select
          options={[
            { value: '', label: 'Any status' },
            { value: 'OPEN', label: 'Open' },
            { value: 'CLOSED', label: 'Closed' },
          ]}
          value={status}
          onChange={(v) => { setStatus((typeof v === 'string' ? v : '') as '' | 'OPEN' | 'CLOSED'); setPage(1); }}
          placeholder="Any status"
        />
      </div>
      <div className="w-full sm:w-44">
        <Select
          options={[{ value: '', label: 'All cashiers' }, ...cashierOptions]}
          value={cashierId}
          onChange={(v) => setCashierId(typeof v === 'string' ? v : '')}
          placeholder="All cashiers"
        />
      </div>
    </>
  );

  const meta = data?.meta;

  return (
    <ReportPageShell
      title="Register Sessions"
      subtitle="Opening cash, sales, payment modes and drawer closing for every register session, by cashier."
      breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Shifts', href: '/shifts' }, { label: 'Register Sessions' }]}
      accent="brand-indigo"
      headerActions={<ReportExportMenu onExport={handleExport} formats={['csv', 'pdf']} disabled={!sessions.length} />}
      filterBar={
        <ReportFilterBar
          timeRange={timeRange}
          onTimeRangeChange={(v) => { setTimeRange(v); setPage(1); }}
          locationId={locationId}
          onLocationChange={(id) => { setLocationId(id); setRegisterId(''); setPage(1); }}
          extraFilters={extraFilters}
        />
      }
    >
      {isLoading ? (
        <ReportSkeleton kpiCount={4} />
      ) : error ? (
        <ReportError />
      ) : (
        <>
          <ReportKpiRow
            tiles={[
              { id: 'net', title: 'Net Sales', value: money(kpis.net), tone: 'brand', icon: KpiIcons.rupee },
              { id: 'sessions', title: 'Sessions', value: String(sessions.length), subLabel: `${kpis.bills} bills`, tone: 'indigo', icon: KpiIcons.receipt },
              { id: 'returns', title: 'Returns', value: money(kpis.returns), tone: 'warning', icon: KpiIcons.tag },
              { id: 'diff', title: 'Cash Difference', value: money(kpis.diff), tone: kpis.diff === 0 ? 'success' : 'danger', icon: KpiIcons.wallet },
            ]}
          />

          {sessions.length === 0 ? (
            <div className="rounded-2xl bg-white dark:bg-neutral-800 shadow-md p-10 text-center text-sm text-neutral-500 dark:text-neutral-400">
              No register sessions for the selected filters.
            </div>
          ) : (
            <div className="space-y-5">
              {sessions.map((s) => (
                <SessionCard key={s.id} session={s} money={money} cashierFiltered={!!cashierId} />
              ))}
            </div>
          )}

          {meta && meta.totalPages > 1 && (
            <div className="flex items-center justify-end gap-3 mt-6 text-sm print:hidden">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="rounded-lg px-3 py-1.5 bg-white dark:bg-neutral-800 shadow-sm hover:bg-brand-50 disabled:opacity-40 transition-all duration-200"
              >
                Previous
              </button>
              <span className="text-neutral-500 dark:text-neutral-400 tabular-nums">
                Page {meta.page} of {meta.totalPages}
              </span>
              <button
                type="button"
                disabled={page >= meta.totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="rounded-lg px-3 py-1.5 bg-white dark:bg-neutral-800 shadow-sm hover:bg-brand-50 disabled:opacity-40 transition-all duration-200"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </ReportPageShell>
  );
}

function SessionCard({
  session: s,
  money,
  cashierFiltered,
}: {
  session: RegisterSessionReportRow;
  money: (minor: number | null | undefined) => string;
  cashierFiltered: boolean;
}) {
  const totals = s.totals;
  const diff = s.drawer.difference;
  return (
    <section
      aria-label={`Session ${s.session_number}`}
      className="rounded-2xl bg-white dark:bg-neutral-800/80 border border-neutral-100 dark:border-neutral-700 shadow-md hover:shadow-lg transition-all duration-200 p-5 animate-slide-up break-inside-avoid"
    >
      <header className="flex flex-wrap items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-brand-500 to-indigo-500 flex items-center justify-center text-white text-xs font-bold">
          {s.register_name.slice(0, 2).toUpperCase()}
        </div>
        <div className="min-w-0">
          <h2 className="text-base font-bold text-neutral-900 dark:text-white">
            {s.register_name} · <span className="font-mono">{s.session_number}</span>
          </h2>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            {s.location_name} · Opened {fmtDateTime(s.opened_at)} by {s.opened_by_name}
            {s.closed_at ? ` · Closed ${fmtDateTime(s.closed_at)}${s.closed_by_name ? ` by ${s.closed_by_name}` : ''}` : ''}
          </p>
        </div>
        <span
          className={
            s.status === 'OPEN'
              ? 'ml-auto rounded-full bg-success-100 dark:bg-success-900/40 px-2.5 py-0.5 text-xs font-semibold text-success-700 dark:text-success-300'
              : 'ml-auto rounded-full bg-neutral-100 dark:bg-neutral-700 px-2.5 py-0.5 text-xs font-semibold text-neutral-600 dark:text-neutral-300'
          }
        >
          {s.status === 'OPEN' ? 'Open' : 'Closed'}
        </span>
      </header>

      {!cashierFiltered && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-4">
          <Figure label="Bills" value={String(totals.sales_count)} />
          <Figure label="Sales" value={money(totals.sales_total)} />
          <Figure label="Customers" value={String(totals.customers_count)} />
          <Figure label="Discount" value={money(totals.discount_total)} />
          <Figure label="Tax" value={money(totals.tax_total)} />
          <Figure label={`Returns (${totals.returns_count})`} value={money(totals.returns_total)} tone="warning" />
          <Figure label={`Cancelled (${totals.cancelled_count})`} value={money(totals.cancelled_total)} tone="danger" />
          <Figure label="Net" value={money(totals.net_total)} tone="brand" />
        </div>
      )}

      {!cashierFiltered && s.payment_modes.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {s.payment_modes.map((p) => (
            <span
              key={p.payment_method}
              className="rounded-full bg-indigo-50 dark:bg-indigo-900/30 px-3 py-1 text-xs font-medium text-indigo-700 dark:text-indigo-300 tabular-nums"
            >
              {p.payment_method} {money(p.collected)}
              {p.refunded ? ` · refunded ${money(p.refunded)}` : ''}
            </span>
          ))}
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-neutral-100 dark:border-neutral-700">
        <table className="min-w-full text-sm">
          <thead className="bg-neutral-50 dark:bg-neutral-900/60 text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
            <tr>
              <th className="px-3 py-2 text-left">Cashier</th>
              <th className="px-3 py-2 text-right">Bills</th>
              <th className="px-3 py-2 text-right">Sales</th>
              <th className="px-3 py-2 text-right">Discount</th>
              <th className="px-3 py-2 text-right">Tax</th>
              <th className="px-3 py-2 text-right">Returns</th>
              <th className="px-3 py-2 text-right">Cancelled</th>
              <th className="px-3 py-2 text-right">Net</th>
              <th className="px-3 py-2 text-left">Payment modes</th>
            </tr>
          </thead>
          <tbody>
            {s.cashiers.map((c) => (
              <tr
                key={c.cashier_id ?? 'none'}
                className="even:bg-neutral-50 dark:even:bg-neutral-900/30 hover:bg-brand-50 dark:hover:bg-neutral-700/40 transition-colors"
              >
                <td className="px-3 py-2 font-medium text-neutral-900 dark:text-white">{c.cashier_name ?? 'Unknown'}</td>
                <td className="px-3 py-2 text-right tabular-nums">{c.sales_count}</td>
                <td className="px-3 py-2 text-right tabular-nums">{money(c.sales_total)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{money(c.discount_total)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{money(c.tax_total)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-warning-600">{money(c.returns_total)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-danger-600">{c.cancelled_count}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold">{money(c.net_total)}</td>
                <td className="px-3 py-2 text-xs text-neutral-600 dark:text-neutral-300">
                  {c.payment_modes.map((p) => `${p.payment_method} ${money(p.collected - p.refunded)}`).join(' · ') || '—'}
                </td>
              </tr>
            ))}
            {s.cashiers.length === 0 && (
              <tr>
                <td colSpan={9} className="px-3 py-4 text-center text-neutral-500">No bills on this session yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {!cashierFiltered && (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 rounded-xl bg-gradient-to-r from-teal-50 to-success-50 dark:from-teal-900/20 dark:to-success-900/20 p-4">
          <Figure label="Opening cash" value={money(s.drawer.opening_cash)} />
          <Figure label={s.status === 'OPEN' ? 'Expected now' : 'Expected cash'} value={money(s.drawer.expected_cash)} />
          <Figure label="Counted cash" value={money(s.drawer.counted_cash)} />
          <Figure
            label="Difference"
            value={money(diff)}
            tone={diff == null ? undefined : diff === 0 ? 'success' : 'danger'}
            note={
              s.drawer.difference_reason
                ? `${s.drawer.difference_reason}${s.drawer.approved_by_name ? ` · approved by ${s.drawer.approved_by_name}` : ''}`
                : undefined
            }
          />
        </div>
      )}
    </section>
  );
}

function Figure({
  label,
  value,
  tone,
  note,
}: {
  label: string;
  value: string;
  tone?: 'brand' | 'success' | 'warning' | 'danger';
  note?: string;
}) {
  const toneClass =
    tone === 'brand'
      ? 'text-brand-700 dark:text-brand-300'
      : tone === 'success'
        ? 'text-success-600 dark:text-success-400'
        : tone === 'warning'
          ? 'text-warning-600 dark:text-warning-400'
          : tone === 'danger'
            ? 'text-danger-600 dark:text-danger-400'
            : 'text-neutral-900 dark:text-white';
  return (
    <div>
      <p className="text-[11px] font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">{label}</p>
      <p className={`text-sm font-bold tabular-nums ${toneClass}`}>{value}</p>
      {note && <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">{note}</p>}
    </div>
  );
}
